<template>
  <div class="related-articles__wrapper">
    <h2 v-if="allArticles.length" class="related-articles__heading">
      Related Articles
    </h2>

    <a-articles-list-with-banners
      v-on="$listeners"
      :articles="topArticleList"
    />

    <a-visibility show :to="$breakpoint.desktopMd">
      <a-related-terms
        :related-terms="relatedTerms"
        :directory-categories="directoryCategories"
      />
    </a-visibility>

    <a-vue-disqus
      v-if="termCanonicalUrl"
      class="related-articles__disqus"
      :title="termName"
      :identifier="termId"
      :url="termCanonicalUrl"
      :category_id="$env.FM_DISQUS_TERM_ID"
    />

    <a-articles-list-with-banners
      v-if="isBottomArticlesVisible"
      :articles="bottomArticleList"
      v-on="$listeners"
    />

    <a-see-more-less
      :page="pagination.page"
      :pages-count="pagesCount"
      :is-expanded="false"
      pagination
      :in-progress="inProgress"
      class="related-articles__see-more-less"
      @input="$emit('get-new-chunk')"
    />
    <p v-if="isNoNewsMessageVisible" class="news-list__no-news-found">
      No news found
    </p>
  </div>
</template>

<script>
import {
  isPaginationValid,
  PROP_TYPES,
  propValidator
} from '@fmpedia/validators'
import mixins from '@/utils/mixins'
import AArticlesListWithBanners from './AArticlesListWithBanners'
import { NEWS_ITEM_DATA_ATTRIBUTE } from 'enums/data-attributes'
import { ARTICLE_SLOT_CLASS_NAME } from 'shared/APaginatedNewsList/enums'
import { ACTION_ICON } from 'shared/ANewsItem'
import { hydrateWhenVisible } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'

const TOP_LIST_LENGTH = 3

export default {
  name: 'ARelatedArticles',
  mixins: [mixins.urlGenerators],
  components: {
    ASeeMoreLess: hydrateWhenVisible(() => import('shared/ASeeMoreLess'), {
      props: ['page', 'pages-count', 'is-expanded', 'pagination', 'in-progress']
    }),
    AVueDisqus: () => import('shared/AVueDisqus/lazy'),
    AArticlesListWithBanners,
    ARelatedTerms: hydrateWhenVisible(
      () => import('@/components/_sections/terms/ARelatedTerms'),
      { props: ['related-terms', 'directory-categories'] }
    )
  },
  model: {
    prop: 'pagination',
    event: 'pagination-change'
  },
  props: {
    pagination: propValidator([PROP_TYPES.OBJECT], true, null, value =>
      isPaginationValid(value)
    ),
    allArticles: propValidator([PROP_TYPES.ARRAY]),
    relatedTerms: propValidator([PROP_TYPES.ARRAY]),
    directoryCategories: propValidator([PROP_TYPES.ARRAY]),
    pagesCount: propValidator([PROP_TYPES.NUMBER]),
    termId: propValidator([PROP_TYPES.STRING]),
    termName: propValidator([PROP_TYPES.STRING]),
    inProgress: propValidator([PROP_TYPES.BOOLEAN])
  },
  consts: {
    NEWS_ITEM_DATA_ATTRIBUTE,
    RELATED_ARTICLES_ACTION_ICONS: [ACTION_ICON.SAVE, ACTION_ICON.SHARE],
    ARTICLE_SLOT_SELECTOR: `.${ARTICLE_SLOT_CLASS_NAME}`
  },
  computed: {
    topArticleList() {
      return this.allArticles.slice(0, TOP_LIST_LENGTH)
    },
    bottomArticleList() {
      return this.allArticles.slice(TOP_LIST_LENGTH)
    },
    isBottomArticlesVisible() {
      return this.allArticles.length > TOP_LIST_LENGTH
    },
    /**
     * ToDo: Ask Backend to add Slug to the response of get-term route
     * to use it instead of route param.
     */
    termCanonicalUrl() {
      const Slug = this.$route.params.slug

      if (!Slug) return null

      return this.generateTermUrl({ Slug })
    },
    isNoNewsMessageVisible() {
      return (
        !this.inProgress && this.allArticles && this.allArticles.length === 0
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.related-articles__wrapper {
  .related-articles__heading {
    margin-bottom: 10px;
  }

  .related-articles__disqus {
    margin-bottom: 40px;

    @include mobile {
      margin-bottom: 20px;
    }
  }

  .related-articles__see-more-less {
    margin-top: 20px;
  }
}
</style>
